"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiCreateOrUpdateProfileCicStatement, CicStatement } from "@/entities/IProfile";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { STATEMENT_GROUP, STATEMENT_TYPE } from "@/helpers/Types";
import { commonApiPost } from "@/services/api/common-api";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { useKeyPressEvent } from "react-use";
import UserPageHeaderAboutEditError from "./UserPageHeaderAboutEditError";
export default function UserPageHeaderAboutEdit(t0) {
  const $ = _c(62);
  const {
    profile,
    statement,
    onClose
  } = t0;
  const {
    onProfileStatementAdd
  } = useContext(ReactQueryWrapperContext);
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  useKeyPressEvent("Escape", onClose);
  const inputRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        const {
          value
        } = inputRef.current;
        inputRef.current.setSelectionRange(value.length, value.length);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [value_0, setValue] = useState(statement?.statement_value ?? "");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      setErrorMsg(null);
      const newValue = event.target.value;
      if (newValue.length > 500) {
        setValue(newValue.substring(0, 500));
        return;
      }
      setValue(newValue);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleInputChange = t3;
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  let t4;
  if ($[3] !== loading || $[4] !== statement?.statement_value || $[5] !== value_0) {
    t4 = () => {
      if (!value_0) {
        setIsDisabled(true);
        return;
      }
      if (statement?.statement_value === value_0) {
        setIsDisabled(true);
        return;
      }
      if (loading) {
        setIsDisabled(true);
        return;
      }
      setIsDisabled(false);
    };
    $[3] = loading;
    $[4] = statement?.statement_value;
    $[5] = value_0;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== loading || $[8] !== statement || $[9] !== value_0) {
    t5 = [value_0, statement, loading];
    $[7] = loading;
    $[8] = statement;
    $[9] = value_0;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== profile) {
    t6 = async statement_0 => {
      setLoading(true);
      setErrorMsg(null);
      return await commonApiPost({
        endpoint: `profiles/${profile.query}/cic/statements`,
        body: {
          statement_group: STATEMENT_GROUP.GENERAL,
          statement_type: STATEMENT_TYPE.BIO,
          statement_comment: null,
          statement_value: statement_0
        }
      });
    };
    $[11] = profile;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onClose || $[14] !== onProfileStatementAdd || $[15] !== profile || $[16] !== setToast) {
    t7 = () => {
      setErrorMsg(null);
      setToast({
        message: "About statement added.",
        type: "success"
      });
      onProfileStatementAdd({
        profile
      });
      onClose();
    };
    $[13] = onClose;
    $[14] = onProfileStatementAdd;
    $[15] = profile;
    $[16] = setToast;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = error => {
      setErrorMsg(error as unknown as string);
    };
    t9 = () => {
      setLoading(false);
    };
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t7) {
    t10 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: t8,
      onSettled: t9
    };
    $[20] = t6;
    $[21] = t7;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const addStatementMutation = useMutation(t10);
  let t11;
  if ($[23] !== addStatementMutation || $[24] !== requestAuth || $[25] !== value_0) {
    t11 = async event_0 => {
      event_0.preventDefault();
      if (!value_0) {
        return;
      }
      const {
        success
      } = await requestAuth();
      if (!success) {
        return;
      }
      await addStatementMutation.mutateAsync(value_0);
    };
    $[23] = addStatementMutation;
    $[24] = requestAuth;
    $[25] = value_0;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const onSubmit = t11;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      resize: "none"
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== value_0) {
    t13 = <textarea className="tw-form-input tw-appearance-none tw-block tw-w-full lg:tw-max-w-md lg:tw-w-auto xl:tw-max-w-none tw-rounded-lg tw-border-0 tw-py-3 tw-px-3 tw-bg-iron-900 tw-text-iron-50 tw-font-normal tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 placeholder:tw-text-iron-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 hover:tw-ring-iron-600 tw-text-base tw-transition tw-duration-300 tw-ease-out" name="profile-about" id="profile-about-input" cols={60} rows={4} required={true} value={value_0} onChange={handleInputChange} ref={inputRef} style={t12} />;
    $[28] = value_0;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== value_0.length) {
    t14 = <div className="tw-text-sm tw-text-iron-500">{value_0.length}/{500}</div>;
    $[30] = value_0.length;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = `tw-w-full sm:tw-w-auto tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-border tw-border-solid tw-border-iron-700 tw-rounded-lg tw-transition tw-duration-300 tw-ease-out ${loading ? "tw-cursor-not-allowed" : "hover:tw-bg-iron-800 hover:tw-border-iron-700"}`;
  let t16;
  if ($[32] !== loading || $[33] !== onClose || $[34] !== t15) {
    t16 = <button disabled={loading} onClick={onClose} type="button" className={t15}>Cancel</button>;
    $[32] = loading;
    $[33] = onClose;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = `tw-w-full sm:tw-w-auto tw-flex tw-items-center tw-justify-center tw-relative tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-border tw-border-solid tw-rounded-lg tw-transition tw-duration-300 tw-ease-out ${isDisabled ? "tw-cursor-not-allowed tw-text-iron-500 tw-bg-iron-800 tw-border-iron-800" : "tw-cursor-pointer tw-text-white tw-bg-primary-500 tw-border-primary-500 hover:tw-bg-primary-600 hover:tw-border-primary-600"}`;
  const t18 = loading ? "hidden" : "visible";
  let t19;
  if ($[36] !== t18) {
    t19 = <div style={{
      visibility: t18
    }}>Save</div>;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== loading) {
    t20 = loading && <svg aria-hidden="true" role="output" className="tw-inline tw-w-5 tw-h-5 tw-text-primary-400 tw-animate-spin tw-absolute" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path className="tw-text-iron-600" d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" /></svg>;
    $[38] = loading;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== isDisabled || $[41] !== t17 || $[42] !== t19 || $[43] !== t20) {
    t21 = <button disabled={isDisabled} type="submit" className={t17}>{t19}{t20}</button>;
    $[40] = isDisabled;
    $[41] = t17;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t16 || $[46] !== t21) {
    t22 = <div className="tw-mt-3 tw-inline-flex tw-space-x-3">{t16}{t21}</div>;
    $[45] = t16;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t14 || $[49] !== t22) {
    t23 = <div className="tw-mt-1 lg:tw-mt-0 tw-inline-flex tw-flex-wrap tw-w-full tw-justify-between">{t14}{t22}</div>;
    $[48] = t14;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== onSubmit || $[52] !== t13 || $[53] !== t23) {
    t24 = <form onSubmit={onSubmit}>{t13}{t23}</form>;
    $[51] = onSubmit;
    $[52] = t13;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== errorMsg) {
    t25 = errorMsg && <UserPageHeaderAboutEditError msg={errorMsg} closeError={() => setErrorMsg(null)} />;
    $[55] = errorMsg;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t25) {
    t26 = <AnimatePresence mode="wait" initial={false}>{t25}</AnimatePresence>;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t24 || $[60] !== t26) {
    t27 = <div className="tw-w-full tw-flex tw-flex-col-reverse lg:tw-flex-row tw-gap-y-4 lg:tw-gap-x-8 lg:tw-items-start">{t24}{t26}</div>;
    $[59] = t24;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}